import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';

import {Bar, Button, Panel, ThemeProvider} from '@pexip/components';
import {reverseDeviceId} from '@pexip/media-control';
import {
    AudioMeter,
    DevicesList,
    PanelHeader,
    SelfViewSettings,
    usePreviewAnalyzer,
    usePreviewControllerHandler,
    usePreviewErrorHandling,
    useDeviceStatusInfo,
} from '@pexip/media-components';
import type {PreviewStreamController} from '@pexip/media';

import {
    usePreviewAudioInput,
    usePreviewAudioOutput,
    usePreviewController,
    usePreviewVideoInput,
} from '../../contexts/media.context';
import {
    useDevices,
    useMediaStatus,
    useHaveRequestedMedia,
} from '../../hooks/userMedia';
import {useConfig} from '../../contexts/config.context';
import {DEFAULT_HEIGHT, DEFAULT_WIDTH} from '../../constants';
import {TestId} from '../../../test/testIds';

export const VideoAndSound: React.FC<{
    onBackClick: () => void;
    close: (e: React.SyntheticEvent<HTMLElement>) => void;
}> = ({close, onBackClick}) => {
    const controller = usePreviewController(true);
    return controller ? (
        <PreviewControllerHandler
            controller={controller}
            close={close}
            onBackClick={onBackClick}
        />
    ) : null;
};

const PreviewControllerHandler: React.FC<{
    controller: PreviewStreamController;
    onBackClick: () => void;
    close: (e: React.SyntheticEvent<HTMLElement>) => void;
}> = ({close, onBackClick, controller}) => {
    const {t} = useTranslation();

    const devices = useDevices();
    const streamStatus = useMediaStatus();
    const requested = useHaveRequestedMedia();

    const {
        videoInputError,
        setVideoInputError,
        audioInputError,
        setAudioInputError,
    } = usePreviewErrorHandling(controller, streamStatus, requested);

    const audioInput = usePreviewAudioInput({
        error: audioInputError,
        controller,
        setError: setAudioInputError,
    });

    const videoInput = usePreviewVideoInput({
        error: videoInputError,
        controller,
        setError: setVideoInputError,
    });

    const audioOutput = usePreviewAudioOutput(
        devices,
        useConfig('audioOutput')[0],
    );

    const {
        handleSave,
        isSaving,
        previewMedia,
        status,
        updatingPreviewAudio,
        updatingPreviewVideo,
        videoMuted,
    } = usePreviewControllerHandler({
        controller,
        close,
        applyChanges: () => {
            videoInput.applyChanges();
            audioInput.applyChanges();
            audioOutput.applyChanges();
            return false;
        },
    });

    const deviceStatusInfo = useDeviceStatusInfo(status, requested);
    const analyzer = usePreviewAnalyzer(controller);
    const handleAudioOutputChange = (deviceId: string) => {
        const device = reverseDeviceId(deviceId);
        const [selectedDevice] = devices.get(device);
        if (selectedDevice) {
            audioOutput.setPreview(selectedDevice);
        }
    };
    const allowToSave =
        !isSaving &&
        /*
         * Respective device selection is disabled when requestedVideo or requestedAudio
         * is false, this should not hinder the user from saving other changes.
         */
        (!videoInput.error.title || !requested.video) &&
        (!audioInput.error.title || !requested.audio);

    const requestCameraPreview = useCallback(() => {
        if (controller.media?.videoInput) {
            if (
                controller.media.getVideoTracks().some(track => track.stopped)
            ) {
                controller.updatePreviewInput(controller.media.videoInput);
            } else {
                controller.media.muteVideo(false);
            }
        } else if (controller.media?.expectedVideoInput) {
            controller.updatePreviewInput(controller.media.expectedVideoInput);
        }
    }, [controller]);

    return (
        <Panel
            style={{width: 380}}
            colorScheme="light"
            hasFooterShadow
            headerPadding="none"
            headerContent={
                <ThemeProvider colorScheme="light">
                    <PanelHeader
                        title={`${t(
                            'settings.video-and-sound',
                            'Video and sound',
                        )}`}
                        onBackClick={onBackClick}
                        backButtonTestId={TestId.ButtonSettingsBack}
                    />
                </ThemeProvider>
            }
            footerContent={
                <ThemeProvider colorScheme="light">
                    <Bar>
                        <Button
                            variant="secondary"
                            modifier="fullWidth"
                            onClick={close}
                            isDisabled={isSaving}
                        >
                            Close
                        </Button>
                        <Button
                            modifier="fullWidth"
                            className="ml-2"
                            isLoading={isSaving}
                            isDisabled={!allowToSave}
                            onClick={(e: React.SyntheticEvent<HTMLElement>) => {
                                const _handleSave = async () => {
                                    // FIXME: workaround to keep the same ratio as a main  stream
                                    await controller?.media?.applyConstraints({
                                        video: {
                                            width: DEFAULT_WIDTH,
                                            height: DEFAULT_HEIGHT,
                                        },
                                    });
                                    await handleSave(e);
                                };

                                void _handleSave();
                            }}
                        >
                            Save
                        </Button>
                    </Bar>
                </ThemeProvider>
            }
        >
            <ThemeProvider colorScheme="light">
                <SelfViewSettings
                    localMediaStream={controller.media?.stream}
                    isVideoInputMuted={videoMuted}
                    updatingPreviewAudio={updatingPreviewAudio}
                    updatingPreviewVideo={updatingPreviewVideo}
                    requestCameraPreview={requestCameraPreview}
                    deviceStatusInfo={
                        deviceStatusInfo.error
                            ? deviceStatusInfo
                            : {
                                  title: t(
                                      'settings.turn-on-preview-titile',
                                      'Do you want to see a preview of your camera?',
                                  ),
                                  message: t(
                                      'settings.turn-on-preview-message',
                                      'To show you a preview the app need request access to your camera. Others will not be able to see you until you turn your camera on again.',
                                  ),
                              }
                    }
                />
                {!!previewMedia?.audioInput && !!analyzer && (
                    <div className="mb-2">
                        <AudioMeter analyzer={analyzer} width={374} />
                    </div>
                )}
                <DevicesList
                    devices={devices}
                    disableVideoInputSelector={
                        !requested.video || updatingPreviewVideo
                    }
                    disableAudioInputSelector={
                        !requested.audio || updatingPreviewAudio
                    }
                    disableAudioOutputSelector={false}
                    requestedAudio={requested.audio}
                    requestedVideo={requested.video}
                    videoInputError={videoInput.error}
                    audioInputError={audioInput.error}
                    videoInput={videoInput.preview}
                    audioInput={audioInput.preview}
                    audioOutput={audioOutput.preview}
                    onAudioInputChange={audioInput.setPreview}
                    onAudioOutputChange={handleAudioOutputChange}
                    onVideoInputChange={videoInput.setPreview}
                    isLoading={isSaving}
                />
            </ThemeProvider>
        </Panel>
    );
};
